import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, type TableColumn, Loader } from 'equal-ds-ui';
import {
  RefreshCw, AlertTriangle, Settings, Plus, Trash2, Save,
  X, Edit2, Globe, ChevronLeft, Webhook, FileWarning,
  CheckCircle2, XCircle, Eye, EyeOff, Code, Copy, Check,
} from 'lucide-react';
import { orchestrationConfigAPI } from '../../../services/orchestration-config-api.service';
import { isSmartOrchestrationEnabled } from '../../../utils/feature-flags';
import { WorkflowEditor } from './WorkflowEditor';
import './orchestration-config.css';
import type { OrchestrationProduct, GatewayConfig } from '../../../types/orchestration-config';
import type { PageComponentProps } from '../../../registry/react-component-registry.constants';

// ==================== Toast System ====================

interface ToastItem {
  id: number;
  type: 'success' | 'error';
  message: string;
}

let toastIdCounter = 0;

const ToastContainer: React.FC<{ toasts: ToastItem[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div className="orch-toast-container">
      {toasts.map((t) => (
        <div key={t.id} className={`orch-toast orch-toast-${t.type}`}>
          {t.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
          <span>{t.message}</span>
          <button className="orch-toast-dismiss" onClick={() => onDismiss(t.id)}>
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
};

function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, addToast, dismissToast };
}

// ==================== Confirm Dialog ====================

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ title, message, confirmLabel = 'Delete', onConfirm, onCancel }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div className="orch-dialog-backdrop" onClick={onCancel}>
      <div className="orch-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="orch-dialog-actions">
          <button className="orch-btn" onClick={onCancel}>Cancel</button>
          <button className="orch-btn orch-btn-danger" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

// ==================== Main Page ====================

export const OrchestrationConfigPage: React.FC<PageComponentProps> = () => {
  const [products, setProducts] = useState<OrchestrationProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orchestrationEnabled, setOrchestrationEnabled] = useState<boolean | null>(null);

  // Modal state
  const [selectedProduct, setSelectedProduct] = useState<OrchestrationProduct | null>(null);
  const [modalConfig, setModalConfig] = useState<GatewayConfig | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Enable orchestration dialog state
  const [showEnableDialog, setShowEnableDialog] = useState(false);
  const [enableProductCode, setEnableProductCode] = useState<string>('');
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [enableExchangeAppId, setEnableExchangeAppId] = useState('');
  const [enableInstanceName, setEnableInstanceName] = useState('');
  const [enableHeaderDesc, setEnableHeaderDesc] = useState('');
  const [enableTxnIdResponse, setEnableTxnIdResponse] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [enableCheckResult, setEnableCheckResult] = useState<any>(null); // result from step 1

  useEffect(() => {
    (async () => {
      try {
        const flags = await orchestrationConfigAPI.getFeatureFlags();
        setOrchestrationEnabled(flags.smart_orchestration_enabled);
      } catch {
        setOrchestrationEnabled(isSmartOrchestrationEnabled());
      }
    })();
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await orchestrationConfigAPI.getProducts();
      setProducts(data.products || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch orchestration products');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (orchestrationEnabled === true) fetchProducts();
    else if (orchestrationEnabled === false) setIsLoading(false);
  }, [orchestrationEnabled, fetchProducts]);

  const openConfigModal = useCallback(async (product: OrchestrationProduct) => {
    setSelectedProduct(product);
    setModalLoading(true);
    setModalConfig(null);
    try {
      const config = await orchestrationConfigAPI.getGatewayConfig(product.exchange_application_id);
      setModalConfig(config);
    } catch (err: any) {
      setModalConfig({ orchestration_configuration: null, _error: err.message } as any);
    } finally {
      setModalLoading(false);
    }
  }, []);

  const closeModal = useCallback(() => {
    setSelectedProduct(null);
    setModalConfig(null);
  }, []);

  const { toasts: pageToasts, addToast: addPageToast, dismissToast: dismissPageToast } = useToast();

  const closeEnableDialog = useCallback(() => {
    setShowEnableDialog(false);
    setEnableProductCode('');
    setAllProducts([]);
    setEnableExchangeAppId('');
    setEnableInstanceName('');
    setEnableHeaderDesc('');
    setEnableTxnIdResponse(true);
    setEnableCheckResult(null);
  }, []);

  const openEnableDialog = useCallback(async () => {
    setShowEnableDialog(true);
    setLoadingProducts(true);
    try {
      const data = await orchestrationConfigAPI.getAllActiveProducts();
      setAllProducts((data.products || []).filter((p: any) => !p.has_orchestration));
    } catch (err) {
      addPageToast('error', 'Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, [addPageToast]);

  // Step 1: Check if gateway config exists
  const handleEnableCheck = useCallback(async () => {
    if (!enableProductCode || !enableExchangeAppId.trim()) {
      addPageToast('error', 'Select a product and enter the gateway instance ID');
      return;
    }
    try {
      setEnabling(true);
      const result = await orchestrationConfigAPI.enableOrchestrationForProduct(
        enableProductCode, enableExchangeAppId.trim(), enableTxnIdResponse,
        enableInstanceName.trim() || undefined, enableHeaderDesc.trim() || undefined, false
      );
      setEnableCheckResult(result);
    } catch (err: any) {
      addPageToast('error', err.message || 'Failed to check gateway config');
    } finally {
      setEnabling(false);
    }
  }, [enableProductCode, enableExchangeAppId, enableTxnIdResponse, enableInstanceName, enableHeaderDesc, addPageToast]);

  // Step 2: Confirm and link
  const handleEnableConfirm = useCallback(async () => {
    try {
      setEnabling(true);
      await orchestrationConfigAPI.enableOrchestrationForProduct(
        enableProductCode, enableExchangeAppId.trim(), enableTxnIdResponse,
        enableInstanceName.trim() || undefined, enableHeaderDesc.trim() || undefined, true
      );
      addPageToast('success', enableProductCode + ' added to orchestration');
      closeEnableDialog();
      fetchProducts();
    } catch (err: any) {
      addPageToast('error', err.message || 'Failed to add product');
    } finally {
      setEnabling(false);
    }
  }, [enableProductCode, enableExchangeAppId, enableTxnIdResponse, enableInstanceName, enableHeaderDesc, addPageToast, closeEnableDialog, fetchProducts]);

  const columns = useMemo<TableColumn<OrchestrationProduct>[]>(() => [
    {
      key: 'product_name',
      header: 'Product',
      width: 260,
      render: (_v: any, row: OrchestrationProduct) => (
        <div>
          <span className="text-sm font-medium" style={{ display: 'block', lineHeight: 1.3 }}>{row.product_name}</span>
          <span className="text-xs" style={{ color: '#9ca3af', fontFamily: 'var(--orch-mono, monospace)', letterSpacing: '-0.01em' }}>{row.product_code}</span>
        </div>
      ),
    },
    {
      key: 'exchange_application_id',
      header: 'Gateway Instance',
      width: 240,
      render: (_v: any, row: OrchestrationProduct) => (
        <span className="text-xs" style={{ fontFamily: 'var(--orch-mono, monospace)', color: '#6b7280' }}>{row.exchange_application_id}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 100,
      render: (_v: any, row: OrchestrationProduct) => (
        <span style={{
          fontSize: 12,
          fontWeight: 500,
          padding: '2px 10px',
          borderRadius: 4,
          background: row.status === 'ACTIVE' ? '#ecfdf5' : '#f3f4f6',
          color: row.status === 'ACTIVE' ? '#047857' : '#6b7280',
        }}>{row.status}</span>
      ),
    },
    {
      key: '_actions',
      header: '',
      width: 100,
      render: (_v: any, row: OrchestrationProduct) => (
        <button
          className="orch-btn orch-btn-sm"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); openConfigModal(row); }}
        >
          Configure
        </button>
      ),
    },
  ], [openConfigModal]);

  if (orchestrationEnabled === null) {
    return (
      <div className="p-6">
        <div className="orch-page-header"><h1 className="orch-page-title">Orchestration</h1></div>
        <div className="orch-empty-state">
          <Loader size={24} />
          <span>Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (!orchestrationEnabled) {
    return (
      <div className="p-6">
        <div className="orch-page-header"><h1 className="orch-page-title">Orchestration</h1></div>
        <div className="orch-empty-state">
          <AlertTriangle size={24} style={{ color: '#d97706' }} />
          <span>Smart orchestration is not enabled for this environment.</span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            Set <code style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>feature_flag_smart_orchestration_enabled</code> in config_master to activate.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="orch-page-header">
        <div>
          <h1 className="orch-page-title">Orchestration</h1>
          <p className="orch-page-subtitle">{products.length} product{products.length !== 1 ? 's' : ''} configured</p>
        </div>
        <div className="orch-page-actions">
          <button className="orch-btn" onClick={openEnableDialog}>
            <Plus size={14} />
            Add product
          </button>
          <button className="orch-btn-icon" onClick={fetchProducts} disabled={isLoading} title="Refresh">
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} style={{ color: '#6b7280' }} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="border border-border-default rounded-xl shadow-sm bg-white">
        <Table
          data={products}
          columns={columns}
          loading={isLoading}
          onRowClick={(row: OrchestrationProduct) => openConfigModal(row)}
        />
      </div>

      {/* Config Slide-over Panel */}
      {selectedProduct && (
        <ConfigPanel
          product={selectedProduct}
          config={modalConfig}
          isLoading={modalLoading}
          onClose={closeModal}
          onRefresh={() => openConfigModal(selectedProduct)}
        />
      )}

      {/* Enable Orchestration Dialog */}
      {showEnableDialog && (
        <div className="orch-dialog-backdrop" onClick={closeEnableDialog}>
          <div className="orch-enable-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="orch-enable-dialog-header">
              <div className="orch-enable-dialog-icon">
                <Plus size={20} />
              </div>
              <div>
                <h3>Add product to orchestration</h3>
                <p>Connect a product to its gateway instance.</p>
              </div>
              <button className="orch-btn-icon orch-enable-dialog-close" onClick={closeEnableDialog}>
                <X size={16} />
              </button>
            </div>

            <div className="orch-enable-dialog-body">
              <div className="orch-enable-field">
                <label className="orch-enable-label">Product</label>
                {loadingProducts ? (
                  <div className="orch-enable-loading">Loading products...</div>
                ) : allProducts.length === 0 ? (
                  <div className="orch-enable-empty">All active products are already configured.</div>
                ) : (
                  <select
                    className="orch-enable-select"
                    value={enableProductCode}
                    onChange={(e) => setEnableProductCode(e.target.value)}
                  >
                    <option value="">Select a product...</option>
                    {allProducts.map((p: any) => (
                      <option key={p.product_code} value={p.product_code}>
                        {p.product_code} — {p.product_name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="orch-enable-field">
                <label className="orch-enable-label">Gateway instance ID</label>
                <input
                  className="orch-enable-input"
                  type="text"
                  value={enableExchangeAppId}
                  onChange={(e) => setEnableExchangeAppId(e.target.value)}
                  placeholder="e.g. gateway.equal.eclf_workflow"
                />
                <span className="orch-enable-hint">From your gateway manager configuration, e.g. gateway.equal.eclf_workflow</span>
              </div>
              <div className="orch-enable-field">
                <label className="orch-enable-label">Instance name</label>
                <input
                  className="orch-enable-input"
                  type="text"
                  value={enableInstanceName}
                  onChange={(e) => setEnableInstanceName(e.target.value)}
                  placeholder="e.g. my-fiu"
                  style={{ fontFamily: 'inherit' }}
                />
                <span className="orch-enable-hint">Display name for this gateway instance</span>
              </div>
              <div className="orch-enable-field">
                <label className="orch-enable-label">Header description</label>
                <input
                  className="orch-enable-input"
                  type="text"
                  value={enableHeaderDesc}
                  onChange={(e) => setEnableHeaderDesc(e.target.value)}
                  placeholder="e.g. Verify your identities"
                  style={{ fontFamily: 'inherit' }}
                />
              </div>
              <label className="orch-enable-toggle">
                <input
                  type="checkbox"
                  checked={enableTxnIdResponse}
                  onChange={(e) => setEnableTxnIdResponse(e.target.checked)}
                />
                <span>Include transaction ID in response</span>
              </label>
            </div>

            {/* Confirmation banner */}
            {enableCheckResult && (
              <div className={`orch-enable-confirm ${enableCheckResult.exists ? 'orch-enable-confirm-warn' : 'orch-enable-confirm-info'}`}>
                <span>{enableCheckResult.exists
                  ? `Gateway config "${enableCheckResult.gateway_name}" already exists. Link this product to it?`
                  : 'No gateway config found. A new one will be created from the template.'
                }</span>
              </div>
            )}

            <div className="orch-enable-dialog-footer">
              <button className="orch-btn" onClick={closeEnableDialog}>Cancel</button>
              {!enableCheckResult ? (
                <button
                  className="orch-btn orch-btn-primary"
                  onClick={handleEnableCheck}
                  disabled={enabling || !enableProductCode || !enableExchangeAppId.trim()}
                >
                  {enabling ? 'Checking...' : 'Check & add'}
                </button>
              ) : (
                <button
                  className="orch-btn orch-btn-primary"
                  onClick={handleEnableConfirm}
                  disabled={enabling}
                >
                  {enabling ? 'Linking...' : enableCheckResult.exists ? 'Link product' : 'Create & link'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Page-level toast notifications */}
      <ToastContainer toasts={pageToasts} onDismiss={dismissPageToast} />
    </div>
  );
};

// ==================== CONFIG SLIDE-OVER PANEL ====================

interface ConfigPanelProps {
  product: OrchestrationProduct;
  config: GatewayConfig | null;
  isLoading: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({ product, config, isLoading, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<'webhooks' | 'workflow' | 'json'>('webhooks');
  const [jsonCopied, setJsonCopied] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonDirty, setJsonDirty] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [jsonSaving, setJsonSaving] = useState(false);
  const { toasts, addToast, dismissToast } = useToast();

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Initialize JSON text when config loads
  useEffect(() => {
    if (config) {
      setJsonText(JSON.stringify(config, null, 2));
      setJsonDirty(false);
      setJsonError(null);
    }
  }, [config]);

  const orchConfig = config?.orchestration_configuration || {};
  const webhooks = orchConfig.callback_webhooks || [];
  const workflowConfig = orchConfig.workflow_configuration;

  return (
    <>
      {/* Backdrop */}
      <div className="orch-backdrop" onClick={onClose} />

      {/* Slide-over panel */}
      <div className="orch-config">
        {/* Header */}
        <div className="orch-header">
          <button className="orch-btn" onClick={onClose}>
            <ChevronLeft size={14} />
            Back
          </button>
          <div className="orch-header-divider" />
          <div className="orch-header-info">
            <h1 className="orch-header-title">{product.product_name}</h1>
            <span className="orch-header-subtitle">{product.product_code} / {product.exchange_application_id}</span>
          </div>
          <button
            className="orch-btn-icon"
            onClick={onClose}
            title="Close panel"
            style={{ marginLeft: 'auto' }}
          >
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="orch-center">
            <div className="orch-center-icon">
              <Loader size={24} />
            </div>
            <span>Loading orchestration configuration...</span>
          </div>
        ) : config?._error ? (
          <div className="orch-content">
            <div className="orch-card" style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b' }}>
              <div className="orch-row" style={{ gap: 8 }}>
                <XCircle size={16} />
                <span>{config._error}</span>
              </div>
            </div>
          </div>
        ) : !config?.orchestration_configuration ? (
          <div className="orch-center">
            <div className="orch-center-icon">
              <FileWarning size={24} />
            </div>
            <span>No orchestration configuration found for this product.</span>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="orch-tabs">
              <button
                className={`orch-tab ${activeTab === 'webhooks' ? 'orch-tab-active' : ''}`}
                onClick={() => setActiveTab('webhooks')}
              >
                <Webhook size={14} />
                Callback Webhooks
                <span className="orch-tab-badge">{webhooks.length}</span>
              </button>
              <button
                className={`orch-tab ${activeTab === 'workflow' ? 'orch-tab-active' : ''}`}
                onClick={() => setActiveTab('workflow')}
              >
                <Settings size={14} />
                Workflow Configuration
              </button>
              <button
                className={`orch-tab ${activeTab === 'json' ? 'orch-tab-active' : ''}`}
                onClick={() => setActiveTab('json')}
              >
                <Code size={14} />
                Raw JSON
              </button>
            </div>

            {/* Content */}
            <div className="orch-content">
              {activeTab === 'webhooks' && (
                <WebhooksTab
                  webhooks={webhooks}
                  exchangeAppId={product.exchange_application_id}
                  onSaved={onRefresh}
                  addToast={addToast}
                />
              )}
              {activeTab === 'workflow' && (
                <WorkflowTab
                  config={workflowConfig}
                  exchangeAppId={product.exchange_application_id}
                  onSaved={onRefresh}
                  addToast={addToast}
                />
              )}
              {activeTab === 'json' && (
                <div className="orch-json-view">
                  <div className="orch-json-toolbar">
                    <span className="orch-json-path">{product.exchange_application_id}</span>
                    <div className="orch-row orch-gap-8">
                      <button
                        className="orch-btn orch-btn-sm"
                        onClick={() => {
                          navigator.clipboard.writeText(jsonText);
                          setJsonCopied(true);
                          setTimeout(() => setJsonCopied(false), 2000);
                        }}
                      >
                        {jsonCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                      {jsonDirty && (
                        <button
                          className="orch-btn orch-btn-sm orch-btn-primary"
                          disabled={jsonSaving || !!jsonError}
                          onClick={async () => {
                            try {
                              const parsed = JSON.parse(jsonText);
                              setJsonSaving(true);
                              await orchestrationConfigAPI.updateFullConfig(product.exchange_application_id, parsed);
                              addToast('success', 'Configuration saved');
                              setJsonDirty(false);
                              onRefresh();
                            } catch (err: any) {
                              addToast('error', err.message || 'Failed to save');
                            } finally {
                              setJsonSaving(false);
                            }
                          }}
                        >
                          <Save size={12} /> {jsonSaving ? 'Saving...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                  {jsonError && (
                    <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{jsonError}</div>
                  )}
                  <textarea
                    className="orch-json-pre"
                    value={jsonText}
                    onChange={(e) => {
                      const val = e.target.value;
                      setJsonText(val);
                      setJsonDirty(true);
                      try {
                        JSON.parse(val);
                        setJsonError(null);
                      } catch {
                        setJsonError('Invalid JSON');
                      }
                    }}
                    spellCheck={false}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Toast notifications */}
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </div>
    </>
  );
};

// ==================== WEBHOOKS TAB (CRUD) ====================

interface WebhooksTabProps {
  webhooks: any[];
  exchangeAppId: string;
  onSaved: () => void;
  addToast: (type: 'success' | 'error', message: string) => void;
}

const WebhooksTab: React.FC<WebhooksTabProps> = ({
  webhooks: initialWebhooks, exchangeAppId, onSaved, addToast,
}) => {
  const [webhooks, setWebhooks] = useState<any[]>(initialWebhooks);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [editHeaders, setEditHeaders] = useState<{ header_name: string; header_value: string }[]>([]);
  const [editEvents, setEditEvents] = useState<string[]>(['JOURNEY_COMPLETE']);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [revealedHeaders, setRevealedHeaders] = useState<Record<string, boolean>>({});

  const toggleHeaderVisibility = (key: string) => {
    setRevealedHeaders((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => { setWebhooks(initialWebhooks); setDirty(false); setRevealedHeaders({}); }, [initialWebhooks]);

  const startEdit = (idx: number) => {
    const wh = webhooks[idx];
    setEditIdx(idx);
    setEditUrl(wh.url || '');
    setEditHeaders(wh.headers ? wh.headers.map((h: any) => ({ ...h })) : []);
    setEditEvents(wh.events && wh.events.length > 0 ? [...wh.events] : ['JOURNEY_COMPLETE']);
  };

  const startAdd = () => { setEditIdx(-1); setEditUrl(''); setEditHeaders([]); setEditEvents(['JOURNEY_COMPLETE']); };
  const cancelEdit = () => { setEditIdx(null); };

  const toggleEvent = (event: string) => {
    setEditEvents(prev => prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]);
  };

  const saveEdit = () => {
    const newWh: any = { url: editUrl, headers: editHeaders.filter(h => h.header_name), events: editEvents };
    const updated = [...webhooks];
    if (editIdx === -1) updated.push(newWh);
    else updated[editIdx!] = newWh;
    setWebhooks(updated);
    setEditIdx(null);
    setDirty(true);
  };

  const deleteWebhook = async (idx: number) => {
    const updated = webhooks.filter((_, i) => i !== idx);
    setWebhooks(updated);
    setConfirmDelete(null);
    try {
      setSaving(true);
      await orchestrationConfigAPI.updateWebhooks(exchangeAppId, updated);
      addToast('success', 'Webhook deleted successfully');
      onSaved();
    } catch (err: any) {
      addToast('error', 'Failed to delete: ' + (err.message || 'Unknown error'));
    } finally { setSaving(false); }
  };

  const saveAll = async () => {
    try {
      setSaving(true);
      await orchestrationConfigAPI.updateWebhooks(exchangeAppId, webhooks);
      setDirty(false);
      addToast('success', 'Webhooks saved successfully');
      onSaved();
    } catch (err: any) {
      addToast('error', 'Failed to save: ' + (err.message || 'Unknown error'));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="orch-row-between orch-mb-16">
        <h3 className="orch-section-title" style={{ margin: 0 }}>Callback Webhooks</h3>
        <div className="orch-row orch-gap-8">
          <button className="orch-btn" onClick={startAdd}>
            <Plus size={14} /> Add Webhook
          </button>
          {dirty && (
            <button className="orch-btn orch-btn-primary" onClick={saveAll} disabled={saving}>
              <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Edit / Add form */}
      {editIdx !== null && (
        <div className="orch-card orch-card-blue orch-mb-16">
          <h4 className="orch-section-title">{editIdx === -1 ? 'Add Webhook' : 'Edit Webhook'}</h4>
          <div>
            <label className="orch-label">URL</label>
            <input
              className="orch-input orch-input-mono orch-mb-8"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
          <div className="orch-mb-8">
            <label className="orch-label">Events</label>
            <div className="orch-row orch-gap-12">
              {['JOURNEY_COMPLETE', 'FI_DATA'].map(event => (
                <label key={event} className="orch-enable-toggle" style={{ padding: 0 }}>
                  <input
                    type="checkbox"
                    checked={editEvents.includes(event)}
                    onChange={() => toggleEvent(event)}
                  />
                  <span>{event}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="orch-row-between orch-mb-8">
            <label className="orch-label" style={{ margin: 0 }}>Headers</label>
            <button
              className="orch-btn orch-btn-sm"
              onClick={() => setEditHeaders([...editHeaders, { header_name: '', header_value: '' }])}
            >
              <Plus size={12} /> Add Header
            </button>
          </div>
          {editHeaders.map((h, i) => (
            <div key={i} className="orch-row orch-gap-8 orch-mb-8">
              <input
                className="orch-input"
                style={{ width: '30%' }}
                value={h.header_name}
                onChange={(e) => { const u = [...editHeaders]; u[i] = { ...u[i], header_name: e.target.value }; setEditHeaders(u); }}
                placeholder="Header name"
              />
              <input
                className="orch-input orch-input-mono"
                style={{ flex: 1 }}
                value={h.header_value}
                onChange={(e) => { const u = [...editHeaders]; u[i] = { ...u[i], header_value: e.target.value }; setEditHeaders(u); }}
                placeholder="Header value"
              />
              <button
                className="orch-btn-icon"
                style={{ color: '#dc2626' }}
                onClick={() => setEditHeaders(editHeaders.filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="orch-row orch-gap-8 orch-mt-8">
            <button className="orch-btn orch-btn-primary" onClick={saveEdit}>
              {editIdx === -1 ? 'Add' : 'Update'}
            </button>
            <button className="orch-btn" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      )}

      {/* Webhook list */}
      {webhooks.length === 0 ? (
        <div className="orch-empty">
          <div className="orch-empty-icon">
            <Globe size={28} />
          </div>
          <p>No callback webhooks configured.</p>
          <p className="orch-empty-hint">Click "Add Webhook" to create one.</p>
        </div>
      ) : (
        webhooks.map((wh, idx) => (
          <div key={idx} className="orch-card">
            <div className="orch-row-between orch-mb-8">
              <span className="orch-badge">Webhook {idx + 1}</span>
              <div className="orch-row orch-gap-8">
                <button className="orch-btn orch-btn-sm" onClick={() => startEdit(idx)}>
                  <Edit2 size={12} /> Edit
                </button>
                <button
                  className="orch-btn orch-btn-sm orch-btn-danger"
                  onClick={() => setConfirmDelete(idx)}
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
            <div className="orch-wh-url">{wh.url || '--'}</div>
            {wh.events && wh.events.length > 0 && (
              <div className="orch-row orch-gap-8" style={{ marginTop: 8 }}>
                {wh.events.map((ev: string) => (
                  <span key={ev} className="orch-badge">{ev}</span>
                ))}
              </div>
            )}
            {wh.headers && wh.headers.length > 0 && (
              <div className="orch-wh-headers-section">
                <span className="orch-label">Headers ({wh.headers.length})</span>
                {wh.headers.map((h: any, hIdx: number) => {
                  const headerKey = `${idx}-${hIdx}`;
                  const isRevealed = !!revealedHeaders[headerKey];
                  return (
                    <div key={hIdx} className="orch-wh-header">
                      <span className="orch-wh-header-name">{h.header_name}</span>
                      <span className="orch-wh-header-value" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {h.header_value
                          ? (isRevealed ? h.header_value : '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022')
                          : '--'}
                        {h.header_value && (
                          <button
                            className="orch-btn-icon"
                            onClick={() => toggleHeaderVisibility(headerKey)}
                            title={isRevealed ? 'Hide value' : 'Show value'}
                            style={{ padding: 2, color: '#6b7280', flexShrink: 0 }}
                          >
                            {isRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}

      {/* Confirm delete dialog */}
      {confirmDelete !== null && (
        <ConfirmDialog
          title="Delete Webhook"
          message={`Are you sure you want to delete Webhook ${confirmDelete + 1}? This action cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => deleteWebhook(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

// ==================== WORKFLOW TAB (Full Editor) ====================

interface WorkflowTabProps {
  config: any;
  exchangeAppId: string;
  onSaved: () => void;
  addToast: (type: 'success' | 'error', message: string) => void;
}

const WorkflowTab: React.FC<WorkflowTabProps> = ({
  config, exchangeAppId, onSaved, addToast,
}) => {
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<any>(null);

  useEffect(() => {
    if (config) {
      const obj = typeof config === 'string' ? JSON.parse(config) : config;
      setCurrentWorkflow(obj);
      setDirty(false);
    }
  }, [config]);

  if (!config) {
    return (
      <div className="orch-empty">
        <div className="orch-empty-icon">
          <FileWarning size={28} />
        </div>
        <p>No workflow configuration found.</p>
      </div>
    );
  }

  const handleChange = (updated: any) => {
    setCurrentWorkflow(updated);
    setDirty(true);
  };

  const saveWorkflow = async () => {
    try {
      setSaving(true);
      await orchestrationConfigAPI.updateWorkflow(exchangeAppId, currentWorkflow);
      setDirty(false);
      addToast('success', 'Workflow saved successfully');
      onSaved();
    } catch (err: any) {
      addToast('error', 'Failed to save: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {dirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button className="orch-btn orch-btn-primary" onClick={saveWorkflow} disabled={saving}>
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Workflow'}
          </button>
        </div>
      )}
      {currentWorkflow && (
        <div style={{
          border: '1px solid var(--orch-border, #e5e7eb)',
          borderRadius: 8,
          overflow: 'hidden',
          height: '60vh',
        }}>
          <WorkflowEditor workflow={currentWorkflow} onChange={handleChange} />
        </div>
      )}
    </div>
  );
};
